import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getStudentsMarkingAllByClassIdAndUlId as getStudentsMarkingAllByClassIdAndUlIdService,
  getStudentMarkingByClassIdAndUlId as getStudentMarkingByClassIdAndUlIdService,
  updateStudentMarkingByClassIdAndUlId as updateStudentMarkingByClassIdAndUlIdService,
  updateStudentsBulkMarkingByClassIdAndUlId as updateStudentsBulkMarkingByClassIdAndUlIdService,
  updateStudentsGroupMarkingByClassIdAndUlIdAndLcId as updateStudentsGroupMarkingByClassIdAndUlIdAndLcIdService
} from '../../services/maestro/maestro.service';
import {
  toggleLoadingClassMarkingStudent,
  toggleLoadingGroupMarking,
  toggleLoadingUnitOfLearningStudents,
  updateClassMarkingMode,
  updateClassMarkingStudent,
  updateGroupMarkingStudents,
  updateUnitOfLearningStudents,
  updateUnitOfLearningStudentsMethod
} from '../../store/actions/class-marking.actions';
import { useCallback } from 'react';
import { handlerErrorV2 } from '../../utils/errors';
import MarkingUtils from '../../utils/marking.utils';
import { addMinutes } from 'date-fns';
import { toggleAlertInfo, toggleAlertModule } from '../../store/actions/alert.actions';
import useLearningGrowth from './useLearningGrowth';

const useMarking = () => {
  const { classId, ulId, curriculumId } = useParams();
  const { updateStudentLearningGrowth } = useLearningGrowth();
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {});
  const user = useSelector((state) => state?.session?.user || {});
  const marking = currentCurriculum?.marking || [];
  const levels = currentCurriculum?.levels || [];
  const minMarking = marking.find((el) => el.minValue)?._id || '';
  const maxMarking = marking.find((el) => el.maxValue)?._id || '';
  const mode = useSelector((state) => state?.classMarking?.mode || []);
  const completed = useSelector((state) => state?.classMarking?.completedType);
  const formData = useSelector((state) => state?.class?.unitOfLearning);
  const lcIds = useSelector((state) => state?.classMarking?.dashboard?.lcIds || []);
  const ulstudents = useSelector((state) => JSON.parse(JSON.stringify(state?.classMarking?.students || [])));

  const dispatch = useDispatch();

  const newLogMarking = (
    studentId,
    lcId,
    oldGS = [],
    newGS = [],
    completed = 'completedTeacher',
    type = 'simple'
  ) => {
    const log = {
      id: (new Date().getTime() + Math.random()).toString(),
      studentId,
      classId,
      ulId,
      lcId,
      type,
      isNew: true,
      date: new Date(),
      userCode: user.code,
      userId: user._id || user.userId,
      userName: user.fullName
    };

    const { from, to } = MarkingUtils.generateFromToLogMarking(
      MarkingUtils.sortGranularSteps(levels, oldGS),
      MarkingUtils.sortGranularSteps(levels, newGS),
      completed,
      minMarking
    );
    log.from = from;
    log.to = to;
    return log;
  };

  const onSetMarkingGranularSteps = (granularSteps = [], completed = 'completedTeacher', maxLevel) => {
    const newGranularSteps = MarkingUtils.setMarking(
      levels,
      granularSteps,
      minMarking,
      maxMarking,
      completed,
      maxLevel
    );

    return newGranularSteps;
  };

  const getStudentsMarkingAllByClassIdAndUlId = useCallback(
    async (studentsClass = [], ul, currentCurriculum) => {
      if (!currentCurriculum) {
        return { error: true, message: 'Curriculum not found' };
      }
      let studentsdata = studentsClass.map((student) => {
        return MarkingUtils.mapStudentsGranularSteps(currentCurriculum, student, null, ul);
      });
      dispatch(toggleLoadingUnitOfLearningStudents(true));
      try {
        const response = await getStudentsMarkingAllByClassIdAndUlIdService(
          curriculumId,
          classId,
          ulId,
          studentsClass?.map((student) => student._id)
        );
        if (!response.data && response.error) {
          return { error: true, message: response.error.message };
        } else {
          studentsdata = studentsClass.map((student) => {
            const data = response.data.find((item) => item.studentId === student._id);
            return MarkingUtils.mapStudentsGranularSteps(currentCurriculum, student, data, ul);
          });
        }
      } catch (error) {
        const message = handlerErrorV2(error);
        return { error: true, message };
      } finally {
        dispatch(toggleLoadingUnitOfLearningStudents(false));
        dispatch(updateUnitOfLearningStudents(studentsdata));
      }
    },
    [classId, curriculumId, ulId]
  );

  const getStudentMarkingByClassIdAndUlId = async (student) => {
    let studentData = MarkingUtils.mapStudentsGranularSteps(currentCurriculum, student, null, formData);
    try {
      dispatch(toggleLoadingClassMarkingStudent(true));
      const response = await getStudentMarkingByClassIdAndUlIdService(
        curriculumId,
        classId,
        ulId,
        student?._id
      );

      if (!response.data && response.error) {
        return { error: true, message: response.error.message };
      } else {
        studentData = MarkingUtils.mapStudentsGranularSteps(
          currentCurriculum,
          student,
          response.data,
          formData
        );
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      dispatch(updateClassMarkingStudent(studentData));
      dispatch(toggleLoadingClassMarkingStudent(false));
    }
  };

  const updateStudentMarkingByClassIdAndUlId = async (student, updateSelected) => {
    const currentMode = mode.find((item) => student?._id === item.studentId);
    if (!currentMode) {
      return { error: true, message: 'Student not found' };
    }
    currentMode.mode = 'saving';
    dispatch(updateClassMarkingMode(currentMode, 'update'));
    let type = '';
    if (completed === 'completedTeacher') {
      type = 'teacher';
    }
    if (completed === 'completedStudent') {
      type = 'student';
    }
    if (!type) {
      currentMode.mode = 'error';
      currentMode.message = 'Type not found';
      dispatch(updateClassMarkingMode(currentMode, 'update'));
      return { error: true, message: 'Type not found' };
    }

    try {
      const response = await updateStudentMarkingByClassIdAndUlIdService(
        curriculumId,
        classId,
        ulId,
        student._id,
        type,
        {
          lcs: student.lcs.filter((lc) => lc.updated),
          logMarking: currentMode.logMarking
        }
      );
      if (!response.data && response.error) {
        currentMode.mode = 'error';
        currentMode.message = response.error.message;
        dispatch(updateClassMarkingMode(currentMode, 'update'));
        return { error: true, message: response.error.message };
      } else {
        let studentData = MarkingUtils.mapStudentsGranularSteps(
          currentCurriculum,
          student,
          response.data,
          formData
        );
        if (updateSelected) {
          dispatch(updateClassMarkingStudent(studentData));
          updateStudentLearningGrowth(studentData, completed);
        }
        dispatch(updateUnitOfLearningStudentsMethod('PUT', studentData));
        dispatch(updateClassMarkingMode(currentMode, 'remove'));
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      currentMode.mode = 'error';
      currentMode.message = message;
      dispatch(updateClassMarkingMode(currentMode, 'update'));

      return { error: true, message };
    } finally {
      dispatch(toggleAlertModule());
    }
  };

  const onModeMarking = (student, logMarking) => {
    const fmode = mode.find((item) => item.studentId === student._id);
    let actionData = 'add';
    if (fmode) {
      actionData = 'update';
    }

    dispatch(
      updateClassMarkingMode(
        {
          _id: (new Date().getTime() + Math.random()).toString(),
          mode: 'editing',
          endDate: fmode?.endDate ? fmode.endDate : addMinutes(new Date(), 1),
          studentId: student._id,
          ...student,
          logMarking
        },
        actionData
      )
    );
  };

  const updateStudentsBulkMarkingByClassIdAndUlId = async (students, markingData) => {
    dispatch(
      toggleAlertInfo(
        true,
        'Adding marking students',
        'Please wait as this may take a few minutes',
        'loading'
      )
    );
    try {
      const response = await updateStudentsBulkMarkingByClassIdAndUlIdService(
        curriculumId,
        classId,
        ulId,
        students.map((student) => student._id),
        {
          lcs: markingData.lcs.filter((lc) => lc.updated),
          logMarking: markingData.logMarking
        }
      );
      if (!response.data && response.error) {
        return { error: true, message: response.error.message };
      } else {
        const studentsData = students.map((student) => {
          const data = response.data.find((item) => item.studentId === student._id);
          return MarkingUtils.mapStudentsGranularSteps(currentCurriculum, student, data, formData);
        });
        dispatch(updateUnitOfLearningStudentsMethod('BATCH_PUT', studentsData));
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      dispatch(toggleAlertInfo(false));
      dispatch(toggleAlertModule());
    }
  };

  const onSaveGroupMarking = async (students) => {
    dispatch(
      toggleAlertInfo(
        true,
        'Adding marking students',
        'Please wait as this may take a few minutes',
        'loading'
      )
    );
    dispatch(toggleLoadingGroupMarking(true));

    try {
      // Group students by lcId to send one request per LC
      const studentsByLcId = {};
      students.forEach((s) => {
        if (!studentsByLcId[s.lcId]) {
          studentsByLcId[s.lcId] = [];
        }
        studentsByLcId[s.lcId].push(s);
      });

      const allResponses = [];
      for (const lcId of Object.keys(studentsByLcId)) {
        const response = await updateStudentsGroupMarkingByClassIdAndUlIdAndLcIdService(
          curriculumId,
          classId,
          ulId,
          lcId,
          studentsByLcId[lcId]
        );
        if (!response.data && response.error) {
          return { error: true, message: response.error.message };
        }
        allResponses.push(...(response.data || []));
      }

      const studentsData = ulstudents
        ?.filter((student) => students.some((s) => s.studentId === student.studentId))
        ?.map((student) => {
          const data = allResponses.find((item) => item.studentId === student.studentId);
          if (data) {
            return MarkingUtils.mapStudentsGranularSteps(currentCurriculum, student, data, formData);
          } else {
            return student;
          }
        });
      dispatch(updateUnitOfLearningStudentsMethod('BATCH_PUT', studentsData));
      dispatch(updateGroupMarkingStudents([]));
      return { error: false };
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    } finally {
      dispatch(toggleLoadingGroupMarking(false));
      dispatch(toggleAlertInfo(false));
      dispatch(toggleAlertModule());
    }
  };

  return {
    onSetMarkingGranularSteps,
    getStudentsMarkingAllByClassIdAndUlId,
    getStudentMarkingByClassIdAndUlId,
    newLogMarking,

    updateStudentMarkingByClassIdAndUlId,
    onModeMarking,

    updateStudentsBulkMarkingByClassIdAndUlId,
    onSaveGroupMarking
  };
};

export default useMarking;
